import { ChartLineIcon } from "@phosphor-icons/react/dist/csr/ChartLine";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ClockCounterClockwise";
import { matchPath } from "react-router-dom";
import type { SidebarSection } from "@/app-shell";
import { runTabs } from "./interactive-report/report-panes";
import { loadingRows, placeholders, tabIcon } from "./job-costing-nav";
import {
  LEGACY_REPORTS_PATH,
  PROFITABILITY_PATH,
  profitabilityRunPath,
} from "./routes";
import type { ReportOutput } from "./types";

const SERVICE_TITAN = "ServiceTitan";

export const runRoute = (
  pathname: string,
): { runId: string; pane: string | null } | null => {
  const match = matchPath(`${PROFITABILITY_PATH}/:runId/:pane?`, pathname);
  const runId = match?.params.runId;
  if (!runId || runId === "agents" || runId === "service-titan") return null;
  return { runId, pane: match.params.pane ?? null };
};

/** A ServiceTitan run's tabs. */
export const runPages = ({
  runId,
  tabs,
  current,
}: {
  runId: string;
  tabs: { id: string; title: string }[];
  current: string | null;
}): SidebarSection[] => {
  const active = current && tabs.some((tab) => tab.id === current);
  return tabs.map((tab, index) => ({
    label: tab.title,
    to: `${profitabilityRunPath(runId)}/${encodeURIComponent(tab.id)}`,
    isActive: active ? current === tab.id : index === 0,
    icon: tabIcon(tab.id),
  }));
};

/**
 * The open run's pages: one skeleton until the output arrives, none for a
 * workbook-only run, otherwise its tabs.
 */
export const openRunPages = ({
  runId,
  interactive,
  pending,
  output,
  sheet,
  documents,
  current,
}: {
  runId: string;
  interactive: boolean;
  pending: boolean;
  output: ReportOutput | undefined;
  sheet: boolean;
  documents: boolean;
  current: string | null;
}): SidebarSection[] => {
  if (!output) return interactive && pending ? loadingRows("run", 1) : [];
  return runPages({
    runId,
    tabs: runTabs({
      output,
      workbookTab: "sheet",
      sheet,
      documents,
    }),
    current,
  });
};

/**
 * The latest ServiceTitan runs, the one on screen first when it is older.
 * Expansion is the list's: the open run can be folded, and another run's
 * chevron is answered by `onExpandedChange` (it opens that run).
 */
export const recentRuns = ({
  runs,
  open,
  pathname,
  history,
  historyRow = history,
  isExpanded,
  onExpandedChange,
}: {
  runs: { id: string; label: string }[] | undefined;
  open: { id: string; label: string; pages: SidebarSection[] } | null;
  pathname: string;
  /** Documents mode lists ServiceTitan history apart from its agents. */
  history: boolean;
  /** Whether the reader may open that history (finance, when it is kept). */
  historyRow?: boolean;
  isExpanded: (id: string) => boolean;
  onExpandedChange: (id: string, expanded: boolean) => void;
}): SidebarSection[] => {
  const group = history ? SERVICE_TITAN : "Recent";
  const listed =
    open && !runs?.some((run) => run.id === open.id)
      ? [open, ...(runs ?? [])]
      : runs;
  return [
    ...(history && historyRow
      ? [
          {
            label: "Report history",
            to: LEGACY_REPORTS_PATH,
            isActive: pathname === LEGACY_REPORTS_PATH,
            icon: ClockCounterClockwiseIcon,
            group,
          },
        ]
      : []),
    ...(listed
      ? listed.map((run) => ({
          label: run.label,
          to: profitabilityRunPath(run.id),
          // A run with no tabs (a workbook-only report) is itself the page.
          isActive:
            run.id === open?.id && !open.pages.some((page) => page.isActive),
          icon: ChartLineIcon,
          group,
          expanded: isExpanded(run.id),
          children: run.id === open?.id ? open.pages : undefined,
          onExpandedChange: (expanded: boolean) =>
            onExpandedChange(run.id, expanded),
        }))
      : history
        ? []
        : placeholders(group)),
  ];
};
